import { useEffect, useState } from "react";
import { api } from "../lib/api";

type Pending = {
  id: string;
  code: string;
  customer_id: number | null;
  customer_name: string | null;
  customer_phone_masked: string;
  reward_title: string;
  reward_tier_punches: number;
  issued_at: string;
  expires_at: string;
};

type ConfirmResult = {
  ok: true;
  customer_name: string;
  reward_title: string;
  new_punches: number;
};

function expiresInSec(expires_at: string): number {
  const t = Date.parse(String(expires_at).replace(" ", "T") + "Z");
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, Math.round((t - Date.now()) / 1000));
}

export function OwnerRedeem() {
  const [rows, setRows] = useState<Pending[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [manual, setManual] = useState("");

  async function load() {
    try {
      const r = await api<{ data: Pending[] }>("/api/redemptions/pending");
      setRows(r.data);
      setErr(null);
    } catch (e) {
      setErr(String(e));
    }
  }
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  // Re-tick so expires_in countdown updates without re-fetching
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  async function confirm(code: string) {
    setBusyId(code);
    try {
      const r = await api<ConfirmResult>("/api/redemptions/confirm", {
        method: "POST",
        body: JSON.stringify({ code })
      });
      setRows(rs => (rs || []).filter(x => x.code !== code));
      setToast(`Redeemed "${r.reward_title}" for ${r.customer_name}. Card now ${r.new_punches} punches.`);
      setTimeout(() => setToast(null), 5000);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function confirmManual(e: React.FormEvent) {
    e.preventDefault();
    const code = manual.toUpperCase().trim();
    if (!/^[A-Z0-9]{4,8}$/.test(code)) {
      setErr("Code must be 4-8 letters/digits.");
      return;
    }
    setManual("");
    await confirm(code);
  }

  return (
    <div>
      <div className="flex items-baseline gap-3">
        <h1 className="text-xl font-semibold">Pending redemptions</h1>
        <div className="text-xs text-neutral-500">refreshes every 5s</div>
      </div>

      {toast && (
        <div className="mt-3 rounded-md bg-green-50 border border-green-200 text-green-800 px-3 py-2 text-sm">
          {toast}
        </div>
      )}
      {err && (
        <div className="mt-3 rounded-md bg-red-50 border border-red-200 text-red-800 px-3 py-2 text-sm">
          {err}
        </div>
      )}

      <form onSubmit={confirmManual} className="mt-4 flex gap-2">
        <input
          value={manual}
          onChange={e => setManual(e.target.value)}
          placeholder="Type a 4-char redeem code"
          className="flex-1 border border-neutral-300 rounded-md px-3 py-2 text-sm uppercase tracking-widest"
          maxLength={8}
        />
        <button
          type="submit"
          disabled={!manual.trim()}
          className="px-3 py-2 rounded-md bg-punchy-500 text-white text-sm font-medium disabled:opacity-50"
        >
          Confirm
        </button>
      </form>

      <div className="mt-6 space-y-3">
        {rows == null && <div className="text-neutral-500 text-sm">Loading…</div>}
        {rows && rows.length === 0 && (
          <div className="text-neutral-500 text-sm">No pending redemptions right now.</div>
        )}
        {rows && rows.map(r => {
          const secs = expiresInSec(r.expires_at);
          return (
            <div key={r.id} className="border border-neutral-200 rounded-lg p-4 bg-white flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-lg font-semibold truncate">{r.customer_name ?? "(unnamed)"}</div>
                <div className="text-sm text-neutral-600">
                  {r.reward_title} · code <span className="font-mono">{r.code}</span> · {r.customer_phone_masked}
                </div>
                <div className="text-xs text-neutral-400 mt-1">
                  {secs > 0 ? `expires in ${secs}s` : "expired"}
                </div>
              </div>
              <button
                onClick={() => confirm(r.code)}
                disabled={busyId === r.code || secs === 0}
                className="px-3 py-2 rounded-md bg-green-600 text-white text-sm font-medium disabled:opacity-50"
              >
                {busyId === r.code ? "…" : "Confirm"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
